import { useState, useEffect, useCallback, useMemo, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { Dialog, Transition, Combobox } from '@headlessui/react'
import { 
  FiSearch, FiHome, FiShoppingCart, FiUsers, FiBox, 
  FiSettings, FiFileText, FiPieChart, FiPlus, 
  FiMoon, FiSun, FiGlobe, FiLogOut, FiActivity, FiMessageSquare,
  FiCommand, FiNavigation, FiTag, FiDatabase, FiLayers, FiPrinter
} from 'react-icons/fi'
import { useTranslation } from '../../hooks/useTranslation'
import { useThemeStore } from '../../store/themeStore'
import { useLanguageStore } from '../../store/languageStore'
import { useAuthStore } from '../../store/authStore'
import { menuItems, MenuItem } from '../../data/menuItems'
import { apiClient } from '../../api/client'
import { API_ENDPOINTS } from '../../api/endpoints'
import { useQuery } from '@tanstack/react-query'
import debounce from 'lodash/debounce'

import { usePermissions } from '../../hooks/usePermissions'
import { useMenuStore } from '../../store/menuStore'
import { useModuleStore } from '../../store/moduleStore'
import { filterMenuItems } from '../../utils/moduleHelpers'

interface CommandItem {
  id: string
  title: string
  category: string
  icon: any
  action: () => void
  shortcut?: string
  subtitle?: string
}

export default function CommandPalette() {
  const { isCommandPaletteOpen: isOpen, setIsCommandPaletteOpen: setIsOpen, toggleCommandPalette } = useMenuStore()
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const { translate, language } = useTranslation()
  const { theme, setTheme } = useThemeStore()
  const { setLanguage } = useLanguageStore()
  const { logout } = useAuthStore()
  const { hasPermission, canPerformAction } = usePermissions()
  const isRTL = language === 'ar'
  const modules = useModuleStore()

  // Flatten menu items for easy searching
  const navigationItems = useMemo(() => {
    const flattened: CommandItem[] = []
    
    const traverse = (items: MenuItem[]) => {
      items.forEach(item => {
        if (item.path) {
          // Only add items that the user has permission to access
          if (hasPermission(item.path)) {
            flattened.push({
              id: `nav-${item.path}`,
              title: item.label[language] || item.label.ar,
              category: isRTL ? 'التنقل' : 'Navigation',
              icon: item.icon || FiNavigation,
              action: () => navigate(item.path!)
            })
          }
        }
        if (item.children) {
          traverse(item.children)
        }
      })
    }
    
    traverse(filterMenuItems(menuItems, modules))
    return flattened
  }, [language, isRTL, navigate, hasPermission, modules])

  // System actions
  const systemItems: CommandItem[] = useMemo(() => [
    { id: 'system-theme-dark', title: isRTL ? 'تفعيل الوضع الداكن' : 'Enable Dark Mode', category: isRTL ? 'النظام' : 'System', icon: FiMoon, action: () => setTheme('dark') },
    { id: 'system-theme-light', title: isRTL ? 'تفعيل الوضع الفاتح' : 'Enable Light Mode', category: isRTL ? 'النظام' : 'System', icon: FiSun, action: () => setTheme('light') },
    { id: 'system-lang-ar', title: 'تغيير اللغة للعربية', category: isRTL ? 'النظام' : 'System', icon: FiGlobe, action: () => setLanguage('ar') },
    { id: 'system-lang-en', title: 'Change Language to English', category: isRTL ? 'النظام' : 'System', icon: FiGlobe, action: () => setLanguage('en') },
    { id: 'system-logout', title: translate('auth.logout') || 'Logout', category: isRTL ? 'النظام' : 'System', icon: FiLogOut, action: () => logout() },
  ], [isRTL, setTheme, setLanguage, logout, translate])

  // Search API for Items, Customers, Vendors
  const { data: apiResults, isLoading: isApiLoading } = useQuery<CommandItem[]>({
    queryKey: ['globalSearch', query],
    queryFn: async () => {
      if (query.length < 2) return []
      
      const canViewItems = hasPermission('/items') || canPerformAction('/items', 'read')
      const canViewCustomers = hasPermission('/customers') || canPerformAction('/customers', 'read')
      const canViewVendors = hasPermission('/vendors') || canPerformAction('/vendors', 'read')

      const promises = []
      if (canViewItems) promises.push(apiClient.get(API_ENDPOINTS.items.list, { params: { searchTerm: query, pageSize: 5 } }))
      else promises.push(Promise.resolve({ data: { data: [] } }))

      if (canViewCustomers) promises.push(apiClient.get(API_ENDPOINTS.customers.list, { params: { searchTerm: query, pageSize: 5 } }))
      else promises.push(Promise.resolve({ data: { data: [] } }))

      if (canViewVendors) promises.push(apiClient.get(API_ENDPOINTS.vendors.list, { params: { searchTerm: query, pageSize: 5 } }))
      else promises.push(Promise.resolve({ data: { data: [] } }))
      
      const [itemsRes, customersRes, vendorsRes] = await Promise.allSettled(promises)

      const results: CommandItem[] = []

      if (itemsRes.status === 'fulfilled' && (itemsRes.value as any).data?.data) {
        (itemsRes.value as any).data.data.forEach((item: any) => {
          results.push({
            id: `item-${item.Id || item.id}`,
            title: item.Name || item.name,
            subtitle: `${isRTL ? 'كود:' : 'Code:'} ${item.Code || item.code} | ${isRTL ? 'السعر:' : 'Price:'} ${item.SalePrice || item.salePrice}`,
            category: isRTL ? 'الأصناف' : 'Items',
            icon: FiBox,
            action: () => navigate(`/items/view/${item.Id || item.id}`)
          })
        })
      }

      if (customersRes.status === 'fulfilled' && (customersRes.value as any).data?.data) {
        (customersRes.value as any).data.data.forEach((c: any) => {
          results.push({
            id: `customer-${c.Id || c.id}`,
            title: c.Name || c.name,
            subtitle: c.Phone || c.phone || '',
            category: isRTL ? 'العملاء' : 'Customers',
            icon: FiUsers,
            action: () => navigate(`/customers?id=${c.Id || c.id}`)
          })
        })
      }

      if (vendorsRes.status === 'fulfilled' && (vendorsRes.value as any).data?.data) {
        (vendorsRes.value as any).data.data.forEach((v: any) => {
          results.push({
            id: `vendor-${v.Id || v.id}`,
            title: v.Name || v.name,
            subtitle: v.Phone || v.phone || '',
            category: isRTL ? 'الموردين' : 'Vendors',
            icon: FiUsers,
            action: () => navigate(`/vendors?id=${v.Id || v.id}`)
          })
        })
      }

      return results
    },
    enabled: query.length >= 2,
    staleTime: 60000,
  })

  const filteredNavigation = useMemo(() => 
    navigationItems.filter(item => 
      item.title.toLowerCase().includes(query.toLowerCase())
    ), [navigationItems, query])

  const filteredSystem = useMemo(() => 
    systemItems.filter(item => 
      item.title.toLowerCase().includes(query.toLowerCase())
    ), [systemItems, query])

  const allResults = useMemo(() => {
    return [...filteredNavigation, ...(apiResults || []), ...filteredSystem]
  }, [filteredNavigation, apiResults, filteredSystem])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        toggleCommandPalette()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [toggleCommandPalette])

  return (
    <Transition.Root show={isOpen} as={Fragment} afterLeave={() => setQuery('')} appear>
      <Dialog as="div" className="relative z-[9999]" onClose={setIsOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="mx-auto max-w-2xl transform divide-y divide-white/10 overflow-hidden rounded-[2rem] bg-[#1a1f2e] border border-white/20 shadow-[0_30px_60px_rgba(0,0,0,0.8)] transition-all">
              <Combobox
                onChange={(item: CommandItem) => {
                  item.action()
                  setIsOpen(false)
                }}
              >
                <div className="relative">
                  <FiCommand
                    className={`pointer-events-none absolute top-4 h-6 w-6 text-primary-400 ${isRTL ? 'right-6' : 'left-6'}`}
                    aria-hidden="true"
                  />
                  <Combobox.Input
                    className={`h-14 w-full border-0 bg-transparent ${isRTL ? 'pr-16 pl-6 text-right' : 'pl-16 pr-6 text-left'} text-white text-lg outline-none placeholder:text-white/20 focus:ring-0`}
                    placeholder={isRTL ? 'ابحث عن صفحة، عميل، صنف... (Ctrl+K)' : 'Search for a page, customer, item... (Ctrl+K)'}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                  {isApiLoading && (
                    <div className={`absolute top-4 ${isRTL ? 'left-6' : 'right-6'}`}>
                      <FiActivity className="animate-spin text-primary-400" />
                    </div>
                  )}
                </div>

                {allResults.length > 0 && (
                  <Combobox.Options static className="max-h-[60vh] overflow-y-auto py-4 text-sm custom-scrollbar">
                    {/* Categories Grouping */}
                    {Array.from(new Set(allResults.map(i => i.category))).map((category) => (
                      <div key={category} className="mb-4">
                        <div className={`px-6 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-white/30 ${isRTL ? 'text-right' : 'text-left'}`}>
                          {category}
                        </div>
                        {allResults.filter(i => i.category === category).map((item) => (
                          <Combobox.Option
                            key={item.id}
                            value={item}
                            className={({ active }) =>
                              `relative flex cursor-default select-none items-center px-6 py-3 transition-all ${
                                active ? 'bg-primary-500/20 border-r-4 border-primary-500' : 'text-white/70'
                              } ${isRTL ? 'flex-row-reverse' : 'flex-row'}`
                            }
                          >
                            {({ active }) => (
                              <>
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${active ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/20' : 'bg-white/5 text-white/30'}`}>
                                  <item.icon size={20} />
                                </div>
                                <div className={`flex-auto ${isRTL ? 'mr-4 text-right' : 'ml-4 text-left'}`}>
                                  <div className={`font-bold ${active ? 'text-white' : 'text-white/80'}`}>
                                    {item.title}
                                  </div>
                                  {item.subtitle && (
                                    <div className="text-[10px] text-white/30 uppercase tracking-wider mt-0.5">
                                      {item.subtitle}
                                    </div>
                                  )}
                                </div>
                                {item.shortcut && (
                                  <div className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[9px] font-bold text-white/20 uppercase">
                                    {item.shortcut}
                                  </div>
                                )}
                              </>
                            )}
                          </Combobox.Option>
                        ))}
                      </div>
                    ))}
                  </Combobox.Options>
                )}

                {query !== '' && allResults.length === 0 && !isApiLoading && (
                  <div className="py-20 px-6 text-center">
                    <FiSearch className="mx-auto h-12 w-12 text-white/10 mb-4" />
                    <p className="text-white/40 font-medium">
                      {isRTL ? 'لم يتم العثور على نتائج تطابق بحثك' : 'No results found matching your search.'}
                    </p>
                  </div>
                )}
                
                <div className="px-6 py-3 bg-black/40 border-t border-white/5 flex justify-between items-center text-[10px] text-white/20 uppercase tracking-widest font-black">
                  <div className="flex gap-4">
                    <span>{isRTL ? '↑↓ للتنقل' : '↑↓ Navigate'}</span>
                    <span>{isRTL ? '↵ للفتح' : '↵ Open'}</span>
                  </div>
                  <span>{isRTL ? 'ESC للإغلاق' : 'ESC Close'}</span>
                </div>
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
